import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../components/common/ConfirmModal';
import { getOrg, updateOrg, suspendOrg, activateOrg, impersonateUser, updateUserEmail, sendResetEmail, sendEmail, getOrgStats, getOrgNotes, addOrgNote, deleteOrgNote, getPlans, applyPlan } from '../utils/api';
import { formatDate, formatDistanceToNow } from '../utils/helpers';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Edit2, Save, X, ToggleLeft, ToggleRight,
  Users, Copy, ExternalLink, CheckCircle, XCircle,
  UserCheck, Crown, Mail, Check, Send, KeyRound,
  FileText, Briefcase, FolderOpen, StickyNote, Trash2, Plus, Package, Zap,
} from 'lucide-react';

const CMS_URL = process.env.REACT_APP_CMS_URL || 'https://app.zeuscms.com';

export default function OrgDetailPage() {
  const { id }     = useParams();
  const navigate   = useNavigate();

  const [org,      setOrg]      = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [toggling, setToggling] = useState(false);

  const [form, setForm] = useState({
    subscription_plan: '', subscription_status: '',
    seats_purchased: '', trial_ends_at: '',
  });

  // Email editing state: { [userId]: { value, saving, active } }
  const [emailEdits, setEmailEdits] = useState({});

  const [impersonateModal, setImpersonateModal] = useState(null);
  const [sendEmailModal,   setSendEmailModal]   = useState(null); // { to, name, targetType, targetId, targetLabel }
  const [resetBusy,        setResetBusy]        = useState({});   // { [userId]: true }

  // Stats + Notes
  const [stats,  setStats]  = useState(null);
  const [notes,  setNotes]  = useState([]);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [deletingNote, setDeletingNote] = useState(null);

  // Plan templates
  const [plans,       setPlans]       = useState([]);
  const [applyingPlan, setApplyingPlan] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showApplyPlan, setShowApplyPlan] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, statsData, notesData, plansData] = await Promise.all([
        getOrg(id),
        getOrgStats(id).catch(() => null),
        getOrgNotes(id).catch(() => ({ notes: [] })),
        getPlans().catch(() => []),
      ]);
      setOrg(data);
      setStats(statsData);
      setNotes(notesData.notes || []);
      setPlans(plansData);
      setForm({
        subscription_plan:   data.subscription_plan   || 'trialing',
        subscription_status: data.subscription_status || 'trialing',
        seats_purchased:     data.seats_purchased     || 5,
        trial_ends_at:       data.trial_ends_at ? data.trial_ends_at.split('T')[0] : '',
      });
    } catch { toast.error('Failed to load organization'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleApplyPlan = async (planSlug) => {
    setConfirmAction({
      title: 'Apply Plan',
      message: `Apply the "${planSlug}" plan to ${org?.name}? This will update their seats and set status to active.`,
      confirmLabel: 'Apply Plan',
      danger: false,
      onConfirm: async () => {
        setApplyingPlan(true);
        try {
          await applyPlan(id, planSlug);
          toast.success(`Plan "${planSlug}" applied successfully`);
          setShowApplyPlan(false);
          await load();
        } catch (err) {
          toast.error(err.message || 'Failed to apply plan');
        } finally { setApplyingPlan(false); }
      },
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, seats_purchased: parseInt(form.seats_purchased) || 5 };
      if (payload.trial_ends_at) payload.trial_ends_at = new Date(payload.trial_ends_at).toISOString();
      else payload.trial_ends_at = null;
      const updated = await updateOrg(id, payload);
      setOrg(prev => ({ ...prev, ...updated }));
      setEditing(false);
      toast.success('Organization updated');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleToggle = async () => {
    if (org.is_active) {
      setConfirmAction({
        title: 'Suspend Organization',
        message: `Suspend "${org.name}"? Their users will lose access immediately.`,
        confirmLabel: 'Suspend',
        danger: true,
        onConfirm: async () => {
          setToggling(true);
          try { await suspendOrg(id); toast.success(`${org.name} suspended`); await load(); }
          catch (err) { toast.error(err.message || 'Failed'); }
          finally { setToggling(false); }
        },
      });
      return;
    }
    setToggling(true);
    try { await activateOrg(id); toast.success(`${org.name} reactivated`); await load(); }
    catch (err) { toast.error(err.message || 'Failed'); }
    finally { setToggling(false); }
  };

  const handleImpersonate = async (user) => {
    try {
      const result = await impersonateUser(user.id);
      const url = `${CMS_URL}/impersonate?token=${encodeURIComponent(result.token)}`;
      setImpersonateModal({ name: `${user.first_name} ${user.last_name}`, url });
    } catch (err) {
      toast.error(err.message || 'Failed to impersonate');
    }
  };

  // ── Email edit helpers ───────────────────────────────────────────────────────
  const startEmailEdit = (user) => {
    setEmailEdits(prev => ({
      ...prev,
      [user.id]: { value: user.email, saving: false, active: true },
    }));
  };

  const cancelEmailEdit = (userId) => {
    setEmailEdits(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const saveEmailEdit = async (user) => {
    const edit = emailEdits[user.id];
    if (!edit) return;
    const newEmail = edit.value.trim().toLowerCase();
    if (!newEmail || newEmail === user.email.toLowerCase()) { cancelEmailEdit(user.id); return; }
    if (!newEmail.includes('@')) { toast.error('Enter a valid email address'); return; }

    setEmailEdits(prev => ({ ...prev, [user.id]: { ...prev[user.id], saving: true } }));
    try {
      const updated = await updateUserEmail(user.id, newEmail);
      // Update in local org state
      setOrg(prev => ({
        ...prev,
        users: prev.users.map(u => u.id === user.id ? { ...u, email: updated.email } : u),
      }));
      toast.success(`Email updated to ${updated.email}`);
      cancelEmailEdit(user.id);
    } catch (err) {
      toast.error(err.message || 'Failed to update email');
      setEmailEdits(prev => ({ ...prev, [user.id]: { ...prev[user.id], saving: false } }));
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const note = await addOrgNote(id, noteText.trim());
      setNotes(prev => [note, ...prev]);
      setNoteText('');
      toast.success('Note added');
    } catch (err) { toast.error('Failed to add note'); }
    finally { setAddingNote(false); }
  };

  const handleDeleteNote = async (noteId) => {
    setConfirmAction({
      title: 'Delete Note',
      message: 'Delete this note? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setDeletingNote(noteId);
        try { await deleteOrgNote(id, noteId); setNotes(prev => prev.filter(n => n.id !== noteId)); toast.success('Note deleted'); }
        catch (err) { toast.error('Failed to delete note'); }
        finally { setDeletingNote(null); }
      },
    });
  };

  const handleSendReset = async (user) => {
    setConfirmAction({
      title: 'Send Password Reset',
      message: `Send a password reset email to ${user.email}?`,
      confirmLabel: 'Send Reset',
      danger: false,
      onConfirm: async () => {
        setResetBusy(prev => ({ ...prev, [user.id]: true }));
        try { await sendResetEmail(user.id); toast.success(`Reset email sent to ${user.email}`); }
        catch (err) { toast.error(err.message || 'Failed to send reset email'); }
        finally { setResetBusy(prev => ({ ...prev, [user.id]: false })); }
      },
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <Layout title="Organization" crumb="Loading…">
        <div className="loading-center"><div className="spinner" /> Loading…</div>
      </Layout>
    );
  }

  if (!org) {
    return (
      <Layout title="Organization">
        <div className="empty"><h3>Organization not found</h3></div>
      </Layout>
    );
  }

  const daysLeft = org.trial_ends_at
    ? Math.ceil((new Date(org.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const isTrial = org.subscription_plan === 'trial' || org.subscription_plan === 'trialing';
  const trialExpired = isTrial && daysLeft !== null && daysLeft <= 0;

  const planBadge = {
    trial:      <span className="badge badge-blue">Trial</span>,
    trialing:   <span className="badge badge-blue">Trial</span>,
    early_bird: <span className="badge badge-green">Early Bird</span>,
    core:       <span className="badge badge-dim">Core</span>,
    pro:        <span className="badge badge-gold">Pro</span>,
    elite:      <span className="badge badge-purple">Elite</span>,
    // legacy
    free:       <span className="badge badge-dim">Free</span>,
    starter:    <span className="badge badge-dim">Starter</span>,
    enterprise: <span className="badge badge-purple">Enterprise</span>,
  }[org.subscription_plan] || <span className="badge badge-dim">{org.plan_name || org.subscription_plan?.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) || '—'}</span>;

  const statusBadge = !org.is_active
    ? <span className="badge badge-red">Suspended</span>
    : ({
        active:    <span className="badge badge-green">Active</span>,
        trialing:  <span className="badge badge-blue">Trial</span>,
        past_due:  <span className="badge badge-amber">Past Due</span>,
        cancelled: <span className="badge badge-dim">Cancelled</span>,
      }[org.subscription_status] || <span className="badge badge-dim">{org.subscription_status}</span>);

  const activeUsers = (org.users || []).filter(u => u.is_active);

  return (
    <Layout title="Organization" crumb={org.name}>

      {/* Back + actions */}
      <div className="action-bar" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/orgs')}>
          <ArrowLeft size={13} /> Organizations
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {editing ? (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}><X size={13} /> Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
                <Save size={13} /> {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setSendEmailModal({ to: org.owner_email, name: org.owner_name, targetType: 'org', targetId: org.id, targetLabel: org.name })}
              >
                <Send size={13} /> Email Owner
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}><Edit2 size={13} /> Edit</button>
              <button
                className={`btn btn-sm ${org.is_active ? 'btn-danger' : 'btn-success'}`}
                disabled={toggling}
                onClick={handleToggle}
              >
                {toggling
                  ? <span className="spinner" style={{ width: 12, height: 12 }} />
                  : org.is_active ? <><ToggleLeft size={13} /> Suspend</> : <><ToggleRight size={13} /> Reactivate</>}
              </button>
            </>
          )}
        </div>
      </div>


      {/* ── Data stats bar ── */}
      {stats && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16,
        }}>
          {[
            { icon: Briefcase,  label: 'Total Cases',     value: stats.cases,     sub: `${stats.open_cases} open` },
            { icon: FileText,   label: 'Clients',          value: stats.clients,   sub: `${stats.active_users} active users` },
            { icon: FolderOpen, label: 'Documents',        value: stats.documents, sub: `${stats.recent_cases} cases last 30d` },
          ].map(({ icon: Icon, label, value, sub }) => (
            <div key={label} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--gold-dim)', border: '1px solid var(--border-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={16} style={{ color: 'var(--gold)' }} />
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Firm details */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Firm Details</span>
            <div style={{ display: 'flex', gap: 6 }}>{planBadge}{statusBadge}</div>
          </div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Row label="Firm Name" value={<strong>{org.name}</strong>} />
            <Row label="Org ID"    value={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="uuid">{org.id}</span>
                <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }} onClick={() => copyToClipboard(org.id)}>
                  <Copy size={11} />
                </button>
              </div>
            } />
            <Row label="Owner"   value={<>{org.owner_name} <span className="mono-sm">{org.owner_email}</span></>} />
            <Row label="Phone"   value={org.phone   || '—'} />
            <Row label="Email"   value={org.email   || '—'} />
            <Row label="Address" value={org.address || '—'} />
            <Row label="Joined"  value={formatDate(org.created_at)} />
          </div>
        </div>

        {/* Subscription */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Subscription</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowApplyPlan(p => !p)}>
                <Package size={12} /> Apply Plan
              </button>
              {!editing && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}><Edit2 size={13} /> Edit</button>}
            </div>
          </div>

          {/* Apply Plan quick-picker */}
          {showApplyPlan && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'rgba(212,168,83,0.04)' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                <Zap size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Apply Plan Template
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {plans.filter(p => p.is_active).map(p => {
                  const isCurrent = org?.subscription_plan === p.slug;
                  return (
                    <button key={p.slug}
                      onClick={() => handleApplyPlan(p.slug)}
                      disabled={applyingPlan || isCurrent}
                      style={{
                        padding: '6px 14px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600, cursor: isCurrent ? 'default' : 'pointer',
                        border: `1px solid ${isCurrent ? 'var(--gold)' : 'var(--border)'}`,
                        background: isCurrent ? 'rgba(212,168,83,0.12)' : 'var(--bg-base)',
                        color: isCurrent ? 'var(--gold)' : 'var(--text-primary)',
                        opacity: applyingPlan ? 0.6 : 1,
                        display: 'flex', alignItems: 'center', gap: 5,
                      }}>
                      {isCurrent && <Check size={11} />}
                      {p.name}
                      <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.73rem' }}>
                        {p.price_per_seat_annual === 0 ? 'Free' : `$${p.price_per_seat_annual}/seat/mo`}
                        {' · '}
                        {p.is_unlimited_seats ? '∞ seats' : `${p.seats_included} seats`}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 8 }}>
                Applying a plan sets seats, marks status as active, and clears trial expiry.
              </div>
            </div>
          )}

          <div className="card-body">
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-row">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Plan</label>
                    <select className="form-select" value={form.subscription_plan}
                      onChange={e => setForm(p => ({ ...p, subscription_plan: e.target.value }))}>
                      <option value="trial">Free Trial</option>
                      <option value="early_bird">Early Bird</option>
                      <option value="core">Zeus Core</option>
                      <option value="pro">Zeus Pro</option>
                      <option value="elite">Zeus Elite</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Status</label>
                    <select className="form-select" value={form.subscription_status}
                      onChange={e => setForm(p => ({ ...p, subscription_status: e.target.value }))}>
                      <option value="active">Active</option>
                      <option value="trialing">Trialing</option>
                      <option value="past_due">Past Due</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Seats</label>
                    <input className="form-input" type="number" min={1}
                      value={form.seats_purchased}
                      onChange={e => setForm(p => ({ ...p, seats_purchased: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Trial Ends</label>
                    <input className="form-input" type="date"
                      value={form.trial_ends_at}
                      onChange={e => setForm(p => ({ ...p, trial_ends_at: e.target.value }))} />
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Row label="Plan"     value={<span>{org.plan_name || { trial:'Free Trial', trialing:'Free Trial', core:'Core', pro:'Pro', elite:'Elite', enterprise:'Enterprise', early_bird:'Early Bird', free:'Free', starter:'Starter' }[org.subscription_plan] || (org.subscription_plan ? org.subscription_plan.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) : '—')}</span>} />
                <Row label="Status"   value={<span style={{ textTransform: 'capitalize' }}>{org.subscription_status}</span>} />
                <Row label="Seats"    value={`${activeUsers.length} used / ${org.seats_purchased} purchased`} />
                <Row label="Trial Ends" value={
                  org.trial_ends_at
                    ? <span style={{ color: trialExpired ? 'var(--red)' : daysLeft <= 3 ? 'var(--amber)' : 'inherit' }}>
                        {formatDate(org.trial_ends_at)}
                        {daysLeft !== null && (
                          <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}>
                            {trialExpired ? '(expired)' : `(${daysLeft}d left)`}
                          </span>
                        )}
                      </span>
                    : '—'
                } />
                <Row label="Active" value={org.is_active
                  ? <span style={{ color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 5 }}><CheckCircle size={13} /> Active</span>
                  : <span style={{ color: 'var(--red)',   display: 'flex', alignItems: 'center', gap: 5 }}><XCircle size={13} /> Suspended</span>
                } />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Users table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <Users size={12} style={{ marginRight: 6, verticalAlign: '-1px' }} />
            Users ({(org.users || []).length})
          </span>
          <span className="mono-sm" style={{ color: 'var(--text-muted)' }}>Click the email icon to edit a user's email</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Access</th>
                <th>Status</th>
                <th className="col-hide-mobile">Last Login</th>
                <th>Impersonate</th>
              </tr>
            </thead>
            <tbody>
              {(org.users || []).map(user => {
                const edit = emailEdits[user.id];
                return (
                  <tr key={user.id} style={{ opacity: user.is_active ? 1 : 0.5 }}>

                    {/* Name */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        {user.access_level === 'super_admin' && <Crown size={12} style={{ color: 'var(--gold)', flexShrink: 0 }} />}
                        <span style={{ fontWeight: 500 }}>{user.first_name} {user.last_name}</span>
                      </div>
                    </td>

                    {/* Email — inline edit */}
                    <td>
                      {edit?.active ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="email"
                            value={edit.value}
                            onChange={e => setEmailEdits(prev => ({ ...prev, [user.id]: { ...prev[user.id], value: e.target.value } }))}
                            onKeyDown={e => {
                              if (e.key === 'Enter')  saveEmailEdit(user);
                              if (e.key === 'Escape') cancelEmailEdit(user.id);
                            }}
                            autoFocus
                            style={{
                              background: 'var(--bg-base)', border: '1px solid var(--gold)',
                              borderRadius: 'var(--r-sm)', padding: '4px 8px',
                              fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
                              color: 'var(--text-primary)', outline: 'none', minWidth: 200,
                              boxShadow: '0 0 0 2px var(--gold-dim)',
                            }}
                          />
                          <button
                            className="btn btn-success btn-sm"
                            disabled={edit.saving}
                            onClick={() => saveEmailEdit(user)}
                            title="Save"
                            style={{ padding: '4px 8px' }}
                          >
                            {edit.saving ? <span className="spinner" style={{ width: 11, height: 11 }} /> : <Check size={12} />}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => cancelEmailEdit(user.id)}
                            title="Cancel"
                            style={{ padding: '4px 8px' }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span className="mono" style={{ fontSize: '0.75rem' }}>{user.email}</span>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => startEmailEdit(user)}
                            title="Edit email"
                            style={{ padding: '2px 5px', opacity: 0.5 }}
                          >
                            <Mail size={11} />
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Access */}
                    <td>
                      {user.access_level === 'super_admin' && <span className="badge badge-gold">Super Admin</span>}
                      {user.access_level === 'admin'       && <span className="badge badge-blue">Admin</span>}
                      {(!user.access_level || user.access_level === 'standard') && <span className="badge badge-dim">Standard</span>}
                    </td>

                    {/* Status */}
                    <td>
                      {user.is_paused  ? <span className="badge badge-amber">Paused</span>
                        : user.is_active ? <span className="badge badge-green">Active</span>
                        : <span className="badge badge-red">Inactive</span>}
                    </td>

                    {/* Last Login */}
                    <td className="col-hide-mobile mono-sm">{user.last_login ? formatDistanceToNow(user.last_login) : 'Never'}</td>

                    {/* Actions */}
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {user.is_active && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleImpersonate(user)}
                            title={`Log in as ${user.first_name}`}
                            style={{ color: 'var(--gold)', borderColor: 'var(--border-gold)' }}
                          >
                            <UserCheck size={13} /> Impersonate
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleSendReset(user)}
                          disabled={resetBusy[user.id]}
                          title="Send password reset email"
                        >
                          {resetBusy[user.id]
                            ? <span className="spinner" style={{ width: 11, height: 11 }} />
                            : <KeyRound size={12} />}
                          Reset
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setSendEmailModal({ to: user.email, name: `${user.first_name} ${user.last_name}`, targetType: 'user', targetId: user.id, targetLabel: user.email })}
                          title="Send email to user"
                        >
                          <Send size={12} /> Email
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(org.users || []).length === 0 && (
                <tr><td colSpan={6}>
                  <div className="empty"><Users size={24} /><h3>No users in this organization</h3></div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      {/* ── Internal Notes ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">
            <StickyNote size={12} style={{ marginRight: 6, verticalAlign: '-1px', color: 'var(--gold)' }} />
            Internal Notes
          </span>
          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            visible to system admins only
          </span>
        </div>
        {/* Add note */}
        <div style={{ padding: '12px 16px', borderBottom: notes.length > 0 ? '1px solid var(--border-dim)' : 'none', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <textarea
            className="form-input"
            style={{ flex: 1, resize: 'none', fontFamily: 'var(--font-sans)', fontSize: '0.82rem', lineHeight: 1.6, minHeight: 68 }}
            placeholder='e.g. "Spoke to owner March 10 — interested in upgrading to Enterprise next month."'
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote(); }}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={handleAddNote}
            disabled={addingNote || !noteText.trim()}
            style={{ flexShrink: 0, alignSelf: 'flex-end' }}
          >
            {addingNote ? <span className="spinner" style={{ width: 12, height: 12 }} /> : <Plus size={13} />}
            Add
          </button>
        </div>
        {/* Notes list */}
        {notes.length === 0 ? (
          <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
            No notes yet. Add one above.
          </div>
        ) : (
          notes.map(note => (
            <div key={note.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-dim)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.82rem', lineHeight: 1.7, color: 'var(--text-primary)', marginBottom: 4 }}>
                  {note.body}
                </div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {note.admin_name || note.admin_email} · {formatDate(note.created_at)}
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => handleDeleteNote(note.id)}
                disabled={deletingNote === note.id}
                style={{ color: 'var(--text-muted)', padding: '4px 6px', flexShrink: 0 }}
                title="Delete note"
              >
                {deletingNote === note.id
                  ? <span className="spinner" style={{ width: 11, height: 11 }} />
                  : <Trash2 size={12} />}
              </button>
            </div>
          ))
        )}
      </div>

      {/* Impersonate modal */}
      {impersonateModal && (
        <div className="modal-overlay" onClick={() => setImpersonateModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Impersonate — {impersonateModal.name}</span>
              <button className="btn btn-ghost btn-sm" style={{ padding: '4px 8px' }} onClick={() => setImpersonateModal(null)}>
                <X size={14} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 14 }}>
                This link will log you into the CMS as <strong style={{ color: 'var(--text-primary)' }}>{impersonateModal.name}</strong>.
                The token is valid for <strong style={{ color: 'var(--amber)' }}>1 hour</strong>.
                Open in a private/incognito window to avoid signing out of your admin session.
              </p>
              <div className="impersonate-url">{impersonateModal.url}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className="btn btn-primary" onClick={() => copyToClipboard(impersonateModal.url)}>
                  <Copy size={13} /> Copy Link
                </button>
                <button className="btn btn-ghost" onClick={() => window.open(impersonateModal.url, '_blank', 'noopener')}>
                  <ExternalLink size={13} /> Open in New Tab
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Send Email Modal */}
      {sendEmailModal && (
        <SendEmailModal
          target={sendEmailModal}
          onClose={() => setSendEmailModal(null)}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: '0.62rem', fontWeight: 700,
        letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)',
        width: 90, flexShrink: 0, paddingTop: 1, minWidth: 70,
      }}>{label}</span>
      <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)', flex: 1 }}>{value}</span>
    </div>
  );
}

function SendEmailModal({ target, onClose }) {
  const [subject,  setSubject]  = useState('');
  const [message,  setMessage]  = useState('');
  const [sending,  setSending]  = useState(false);

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Subject required'); return; }
    if (!message.trim()) { toast.error('Message required'); return; }
    setSending(true);
    try {
      await sendEmail({
        to:           target.to,
        subject:      subject.trim(),
        message:      message.trim(),
        target_type:  target.targetType,
        target_id:    target.targetId,
        target_label: target.targetLabel,
      });
      toast.success(`Email sent to ${target.to}`);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title">
            <Send size={13} style={{ marginRight: 6, verticalAlign: '-1px', color: 'var(--gold)' }} />
            Send Email
          </span>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '2px 6px' }}>
            <X size={14} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">To</label>
            <div style={{ padding: '9px 12px', background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              {target.name && target.name !== target.to ? `${target.name} <${target.to}>` : target.to}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Subject</label>
            <input
              className="form-input"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="e.g. Your trial is expiring soon"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea
              className="form-input"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Write your message here…"
              rows={6}
              style={{ resize: 'vertical', fontFamily: 'var(--font-sans)', lineHeight: 1.6 }}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Sending…</> : <><Send size={13} /> Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
}
